import { inject, Service } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiEnums, ApiTypes, httpStatus } from '../../api';
import { ApiClient } from '../../api/api-client';
import { Api } from '../../api/generated/api';
import { CONSTANTS_ERRORS } from '../../common/constants/errors/errors.constants';
import { IDemographicsConfig } from './interfaces/demographics.interface';

/** Where the account's gender options are read from: the Pulse venue or the campaign. */
export type GenderOptionsSource = { venueUuid: string } | { campaignUuid: string };

/** The account defaults, used when the options can't be fetched. */
const DEFAULT_OFFERED_GENDERS: ApiTypes.Gender[] = [
  ApiEnums.Gender.Female,
  ApiEnums.Gender.Male,
  ApiEnums.Gender.PreferNotToSay,
];

@Service()
export class DemographicsService {
  private api = inject(ApiClient);

  isCampaignMode = false;

  /** Disabled until a serve or submit response fills it in; `DemographicsGuard` reads it early. */
  private demographicsConfig: IDemographicsConfig = {
    isEnabled: false,
    shouldAskForGender: false,
    shouldAskForAgeGroup: false,
    headerTranslations: [],
    textTranslations: [],
  };

  private genderOptionsSource: GenderOptionsSource | null = null;

  setDemographicsConfig(demographics: IDemographicsConfig): void {
    this.demographicsConfig = demographics;
  }

  setGenderOptionsSource(source: GenderOptionsSource): void {
    this.genderOptionsSource = source;
  }

  getDemographicsConfig(): IDemographicsConfig {
    return this.demographicsConfig;
  }

  /**
   * The genders the account offers, in display order. If they can't be fetched, the defaults are
   * offered instead: the backend accepts every gender value, so no answer is lost.
   */
  fetchOfferedGenders$(): Observable<ApiTypes.Gender[]> {
    const source = this.genderOptionsSource;

    if (!source) {
      return of(DEFAULT_OFFERED_GENDERS);
    }

    const options$ =
      'campaignUuid' in source
        ? this.api.get(Api.surveys.takeaway.campaign.campaigns.genderOptions, {
            pathParams: { campaignUuid: source.campaignUuid },
          })
        : this.api.get(Api.surveys.takeaway.venues.genderOptions, {
            pathParams: { venueUuid: source.venueUuid },
          });

    return options$.pipe(
      map(options => options.filter(option => option.enabled).map(option => option.gender)),
      catchError(() => of(DEFAULT_OFFERED_GENDERS))
    );
  }

  fetchAgeGroups$(): Observable<ApiTypes.AgeGroup[]> {
    return this.api.get(Api.surveys.takeaway.ageGroups, {}).pipe(
      catchError((error: unknown) => {
        if (httpStatus(error) === CONSTANTS_ERRORS.HTTP_404) {
          console.error('Not found');
        }

        return throwError(() => error);
      })
    );
  }
}
